// @ds/display/exportSpec — how a DS component declares what it means in Excel.
//
// The exporter used to require every field def to hand-declare `exportAs`.
// Nothing ever did (264 field defs, zero declarations), so every entity-list
// export came out as locale-formatted text: "1 234,56 zł" instead of 1234.56,
// "12.08.2026" instead of a date. Asking 264 call sites to repeat what the
// rendering component already knows was the wrong seam.
//
// Instead the knowledge lives ONCE, next to the component that owns the
// formatting — the same rule as every other bit of domain knowledge in the DS:
//
//   AmountDisplay.exportSpec = {
//     type: "currency",
//     value:    (p) => p.value,
//     currency: (p) => p.currency,
//   };
//
// `deriveExportAs` (@ds/tables/helpers/deriveExportAs) reads this off the field
// def's `component`, replays the field's own `data(record)` to get the props,
// and produces the `ExportAs` the exporter wants. A field's explicit `exportAs`
// still wins; a new typed primitive opts in by declaring its own spec, with no
// change to the exporter.
//
// Declared as a static property rather than a component→type table inside the
// exporter so `@ds/tables/helpers/*` never has to import `@ds/connected/*`
// (import cycle) and so the spec can't drift from the props it reads.

import type { ExcelExportType } from "@ds/tables/helpers/excelCellFormat";

/**
 * What a component's rendered value means to a spreadsheet. Every resolver
 * receives the component's fully merged props — exactly what `renderCell`
 * passes to the component itself.
 */
export interface ComponentExportSpec<P = Record<string, unknown>> {
  type: ExcelExportType;
  /** The raw, unformatted value. Excel does the formatting from `numFmt`. */
  value: (props: P) => number | string | Date | null | undefined;
  /** For `currency`: the row's own ISO code. */
  currency?: (props: P) => string | null | undefined;
  /** Per-props numFmt — e.g. a timestamp variant or a non-tonne weight unit. */
  numFmt?: (props: P) => string | undefined;
  /** For `link`: the absolute URL the label should point at. */
  hyperlink?: (props: P) => string | null | undefined;
}

/** A component carrying an export spec. */
export type ExportTaggedComponent<P = Record<string, unknown>> = {
  exportSpec: ComponentExportSpec<P>;
};

/**
 * Attach an export spec to a component. Returns the same component reference
 * (identity matters — field defs compare by component identity), now typed as
 * carrying the spec.
 *
 * Usage keeps the component's own declaration first-class:
 *
 *   export const PriceDisplay = withExportSpec(PriceDisplayImpl, { ... });
 *
 * or, for function declarations, as a post-hoc tag:
 *
 *   withExportSpec(AmountDisplay, { ... });
 */
export function withExportSpec<C, P>(
  component: C,
  spec: ComponentExportSpec<P>,
): C & ExportTaggedComponent<P> {
  const tagged = component as C & ExportTaggedComponent<P>;
  tagged.exportSpec = spec;
  return tagged;
}

/**
 * Read a component's export spec, if it declared one. Accepts `unknown`
 * because field defs erase the component type behind `ComponentType<unknown>`
 * casts at plenty of call sites.
 */
export function readExportSpec(
  component: unknown,
): ComponentExportSpec<Record<string, unknown>> | undefined {
  if (typeof component !== "function" && typeof component !== "object") return undefined;
  if (component == null) return undefined;
  const spec = (component as Partial<ExportTaggedComponent>).exportSpec;
  if (!spec || typeof spec !== "object") return undefined;
  if (typeof spec.value !== "function" || typeof spec.type !== "string") return undefined;
  return spec;
}
